<template>
    <div class="steps" ref="rootRef">
        <slot v-bind="defaultSlotProps"/>
    </div>
</template>

<script lang="ts" setup>
    import {usePortuna} from '@/composables/portuna';
    import {createStepsContext, FormStepsKey} from '@/library/form';
    import {isDeveloper} from '@/utils/portuna';
    import {type ComponentPublicInstance, computed, nextTick, onMounted, provide, ref, watch} from 'vue';
    import {scrollToElement} from '@/library/dom';
    import TopMessageHandler from '@/library/topMessageHandler';
    import {
        getVueChildren,
        identifyElement,
        isComponentName,
        isEmptyObject,
    } from '@/library/utils';
    import {useStore} from 'vuex';

    const portuna = usePortuna();

    const props = withDefaults(
        defineProps<{
            tabBarGroup: string;
            useSteps: boolean;
            stepAsComponent?: boolean;
            disableAutoFocus?: boolean;
        }>(), {
            stepAsComponent: false,
            disableAutoFocus: false,
        },
    );

    const emit = defineEmits<{
        (eventName: 'mounted'): void;
        (eventName: 'changed-tab', data: { name: string; index: number }): void;
        (eventName: 'form-step:validated', state: number): void;
    }>();

    const store = useStore();

    const rootRef = ref<HTMLElement | null>(null);

    const formIsComplete = ref(false);

    const last = computed(() =>
    {
        return labelsCollection.value.length - 1 === ctx.activeIndex.value;
    });

    async function validateContent(index: number, revalidate = true): Promise<void>
    {
        const {
                  content,
                  label,
              } = getElements(index);

        if (!content || !label)
        {
            return Promise.reject('invalid index');
        }

        if (ctx.forceFormIncomplete.value)
        {
            return Promise.reject('forced incomplete');
        }

        const root = content.el.value as HTMLElement | null;

        if (content.controllers.value.length && revalidate)
        {
            await Promise.all(content.controllers.value.map((c) => Promise.resolve(c.updateValue())));
        }

        if (root && revalidate && !content.controllers.value.length)
        {
            const nodes = root.querySelectorAll('.form-element.validate');
            for (const node of Array.from(nodes))
            {
                const formRow: Nullable<HTMLElement> = (node as HTMLElement).closest('.form-row') || null;
                if (!formRow)
                {
                    continue;
                }

                const stack = getVueChildren<Merge<ComponentPublicInstance, { isFormField: Nullable<boolean>, updateValue: FormStepsFieldController['updateValue'] }>>(
                    formRow,
                );

                while (stack.length)
                {
                    const child = stack.shift();
                    if (child?.isFormField && typeof child.updateValue === 'function')
                    {
                        await Promise.resolve(child.updateValue());
                    }

                    try
                    {
                        stack.push(
                            ...getVueChildren<Merge<ComponentPublicInstance, { isFormField: Nullable<boolean>, updateValue: FormStepsFieldController['updateValue'] }>>(
                                identifyElement(child),
                            ),
                        );
                    }
                    catch (e)
                    {
                        if (isDeveloper())
                        {
                            console.error('Couldnt find any vue children...');
                            console.error(e);
                        }
                    }
                }
            }
        }

        if (root)
        {
            const errors = root.getElementsByClassName('form-error');
            const state  = errors.length === 0 ? 1 : 0;
            if (root.children.length)
            {
                emit('form-step:validated', state);

                portuna.emitter?.emit('form-step:validated', state);

                label.validated.value = true;
                label.isValid.value   = state;
            }

            if (errors.length)
            {
                revalidate && TopMessageHandler.error('Es wurden fehlerhafte Eingaben festgestellt, bitte überprüfen Sie diese.');

                scrollToElement(errors[0] as HTMLElement);

                return Promise.reject(errors);
            }
        }

        return Promise.resolve();
    }

    async function openInvalidTab()
    {
        await nextTick();
        await new Promise((r) => setTimeout(r, 100));

        let openTab = -1;
        let index   = 0;

        while (ctx.getLabel(index))
        {
            try
            {
                await validateContent(index, false);
            }
            catch
            {
                if (openTab === -1)
                {
                    openTab = index;
                }
            }

            index++;
        }

        if (openTab >= 0)
        {
            setStep(openTab, true);
        }
    }

    const ctx = createStepsContext({
        stepAsComponent: ref(props.stepAsComponent),
        disableAutoFocus: ref(props.disableAutoFocus),
        validateContent,
        openInvalidTab,
        emitCallback: emit,
        emitter: portuna.emitter
    });

    const first = computed(() => ctx.activeIndex.value === 0);

    const defaultSlotProps = computed(() => ({
        isFirst: first.value,
        isLast: last.value,
        nextStep: ctx.next,
        previousStep: ctx.previous,
        activeTab: activeTab.value,
        labels: labelsCollection.value,
        isComplete: formIsComplete.value && !ctx.forceFormIncomplete.value,
        checkCompletion: checkFormCompletion,
        resetIndex: () =>
        {
            ctx.activeIndex.value = 0;
            updateActiveTab();
        },
        setStep,
        saveCurrentStep,
        setFormIncomplete: (value: boolean) =>
        {
            ctx.forceFormIncomplete.value = value;
        },
    }));

    const options = computed<TabBarData>(() => store.state.tabBar[props.tabBarGroup] || {});

    const activeTab = computed(() => options.value.active);

    const labelsCollection = computed(() =>
    {
        const items: HTMLElement[] = [];
        let index                  = 0;
        while (ctx.getLabel(index))
        {
            const el = ctx.getLabel(index)!.el?.value;
            if (el)
            {
                items.push(el);
            }

            index++;
        }

        return new Proxy(items, {
            get(target, prop)
            {
                if (prop === 'item')
                {
                    return (idx: number) => (target as any)[idx] ?? null;
                }

                return (target as any)[prop as any];
            },
        }) as unknown as HTMLCollectionOf<HTMLDivElement>;
    });

    const requiredFilters = computed(() =>
    {
        const filters                    = (store.state.form as FormState).filter;
        const _requiredFilters: string[] = [];

        for (const key of Object.keys(filters))
        {
            const validators = filters[key]?.validators || [];
            if (validators.some((validator) => validator?.name === 'NotEmpty'))
            {
                _requiredFilters.push(key);
            }
        }

        return _requiredFilters;
    });

    let _completionTimeout: number | undefined;

    async function checkFormCompletion()
    {
        if (_completionTimeout)
        {
            window.clearTimeout(_completionTimeout);
        }

        _completionTimeout = window.setTimeout(async () =>
        {
            const root = rootRef.value;
            if (!root)
            {
                return;
            }

            for (let index = 0; ctx.getContent(index); index++)
            {
                const content = ctx.getContent(index)!;
                if (content.controllers.value.some(controller => controller.isInvalid))
                {
                    formIsComplete.value = false;

                    return;
                }
            }

            for (const element of Array.from(root.querySelectorAll('.form-element.validate')))
            {
                const formRow: Nullable<IFormRowContext> = (element as HTMLElement).closest('.form-row')?.__vue__ || null;
                if (!formRow)
                {
                    continue;
                }

                if (formRow.isInvalid?.value)
                {
                    formIsComplete.value = false;

                    return;
                }

                for (const child of getVueChildren(identifyElement(formRow)))
                {
                    const formElement: any = typeof (child as any).getFormElement === 'function' ? (child as any).getFormElement() : child;
                    if (formElement?.isFormField && (!formElement.modelValue || (typeof formElement.modelValue === 'object' && isEmptyObject(formElement.modelValue))))
                    {
                        if (isComponentName(formElement, 'FormSelect') && !formElement.multiple && formElement.selectedOption !== null)
                        {
                            continue;
                        }

                        if (isComponentName(formElement, 'FormInputFile') && formElement.hasFiles)
                        {
                            continue;
                        }

                        const errorName  = formElement.errorName || formRow._errorName?.value;
                        const filterName = formElement.filterName || formRow._filterName?.value || errorName;

                        if (requiredFilters.value.includes(filterName))
                        {
                            formIsComplete.value = false;

                            return;
                        }
                    }
                }
            }

            formIsComplete.value = true;
        }, 300);
    }

    function scrollSelf()
    {
        if (rootRef.value)
        {
            scrollToElement(rootRef.value);
        }
    }

    function getElements(index: number)
    {
        return {
            label: ctx.getLabel(index),
            content: ctx.getContent(index),
        };
    }

    function setStep(index: number, ignoreValidation = false, force = false)
    {
        if (index > ctx.maxReached.value + 1 && !force)
        {
            return;
        }

        if (index > ctx.maxReached.value)
        {
            ctx.maxReached.value = index;
        }

        const elements = getElements(index);
        const label    = elements.label!;

        if (!label)
        {
            return;
        }

        if (ignoreValidation)
        {
            label.activateTab();

            scrollSelf();

            nextTick(() =>
            {
                const el = elements.content?.el?.value;
                if (el && !props.disableAutoFocus)
                {
                    const first = el.querySelector<HTMLElement>('[tabindex],button,input,select,textarea,a[href]');
                    first?.focus();
                }
            });

            ctx.intention.value = null;
        }
        else
        {
            label.changeTab();
        }

        const data = {
            name: label.name,
            index,
        };

        if (force)
        {
            for (let step = 0; step < index; step++)
            {
                const label = getElements(step).label;
                if (!label)
                {
                    continue;
                }

                label.validated.value = true;
                label.isValid.value   = 1;
            }
        }

        emit('changed-tab', data);

        portuna.emitter?.emit('changed-tab', data);
    }

    function saveCurrentStep()
    {
        const elements = getElements(ctx.activeIndex.value);

        return elements.label!.validateAndSetStep(ctx.activeIndex.value);
    }

    watch(() => options.value.active, () => updateActiveTab());

    async function updateActiveTab()
    {
        ctx.activeIndex.value = -1;
        let index             = 0;

        while (ctx.getLabel(index))
        {
            const label = ctx.getLabel(index)!;
            if (label && options.value.active === label.name)
            {
                ctx.activeIndex.value = index;

                break;
            }

            index++;
        }

        if (ctx.activeIndex.value === -1)
        {
            ctx.activeIndex.value = 0;
            setStep(0, true);
        }
        else if (ctx.activeIndex.value > ctx.maxReached.value)
        {
            ctx.activeIndex.value = ctx.maxReached.value;
            setStep(ctx.maxReached.value, true);
        }
    }

    onMounted(() =>
    {
        if (!props.useSteps)
        {
            ctx.maxReached.value = ctx.labelCount.value;
        }

        updateActiveTab();

        emit('mounted');

        portuna.emitter?.emit('mounted');
    });

    watch(ctx.labelCount, () =>
    {
        if (!props.useSteps)
        {
            ctx.maxReached.value = ctx.labelCount.value;
        }
    });

    provide(FormStepsKey, ctx);

    defineExpose(Object.assign({}, portuna, {
        componentName: 'FormSteps',
        getElements,
        setStep,
        next: ctx.next,
        previous: ctx.previous,
        validateCurrent: ctx.validateCurrent,
        openInvalidTab,
        checkFormCompletion,
    }));
</script>